import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { APIService, AuthService, MenuCategoryDto, MenuItemDto, ModuleMenuDto, NotificationService, PermissionService, SubscriptionMenuDto, SubscriptionMenuService } from "@fourcomply-dashboard/shared";
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { catchError, filter, of, Subscription, take } from "rxjs";
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { MenuCategory, SideNavItem } from '../../models/sidenav-item.interface';
import { UserFavoritesService } from '../../services/user-favorites.service';
import { SideNavItemComponent } from '../sidenav-item/sidenav-item.component';
import { activeTenantCookie, isAbsoluteUrl, isItemVisibleByPermission, isModuleVisibleByPermission, resolveModuleHeaderAction } from './sidenav-module-navigation';
import { AiPanelService } from '../../../../../../apps/fourcomply-dashboard/src/app/shared/services/ai-panel.service';
import { environment } from '../../../environments/environment';
import { productLogoFor } from '../product-logos';


@Component({
  animations: [
    trigger('menuToggle', [
      state('collapsed', style({
        height: '0px',
        opacity: 0,
        overflow: 'hidden'
      })),
      state('expanded', style({
        height: '*',
        opacity: 1
      })),
      transition('expanded <=> collapsed', [
        animate('300ms ease-in-out')
      ])
    ])
  ],
  imports: [
    CommonModule,
    HasPermissionDirective,
    MatExpansionModule,
    MatListModule,
    SideNavItemComponent,
    TranslateModule
  ],
  selector: 'app-sidenav',
  standalone: true,
  styleUrls: ['sidenav.component.scss'],
  templateUrl: 'sidenav.component.html'
})

export class SideNavComponent implements OnInit, OnDestroy {
  is4ComplyEnabled: boolean;
  is4PreferencesEnabled: boolean;
  is4SegmentsEnabled: boolean;
  is4BridgeEnabled: boolean;
  sideNaveUserId: number;
  currentUserId: string = '';

  // Dynamic favorites
  favoriteMenuItems: SideNavItem[] = [];

  // NUEVO: Men� din�mico por suscripci�n
  subscriptionMenu: SubscriptionMenuDto | null = null;
  modules: ModuleMenuDto[] = [];
  loadingMenu = false;
  menuError: string | null = null;

  // localStorage keys for menu state
  private readonly MENU_STATE_KEY = 'sidenav_menu_state';

  private favoritesSubscription: Subscription;
  private menuSubscription: Subscription;
  private permissionsReadySubscription: Subscription;

  /**
   * True once PermissionService's effective-permission set has loaded at least once
   * (PermissionService.ready$). isModulePermitted() below stays open (shows everything the
   * subscription already allows) until this flips — see isModuleVisibleByPermission's doc
   * comment for why. Kept in sync in ngOnInit.
   */
  private permissionsReady = false;

  constructor(
    private api: APIService,
    private auth: AuthService,
    private notification: NotificationService,
    private favoritesService: UserFavoritesService,
    private subscriptionMenuService: SubscriptionMenuService, // NUEVO
    private router: Router,
    private aiPanelService: AiPanelService,
    private permissionService: PermissionService
  ) {

  }

  toggleAiPanel(): void {
    this.aiPanelService.togglePanel();
  }

  ngOnInit() {
    // Load menu state from localStorage for static categories
    this.loadMenuState();

    // Track PermissionService readiness for isModulePermitted() below, and trigger the load
    // ourselves: the sidenav renders on every authenticated page, but only 3 routes carry
    // PermissionGuard (which would otherwise be the only thing calling load()) — without this,
    // most sessions would never have a loaded permission set to filter against.
    this.permissionsReadySubscription = this.permissionService.ready$.subscribe((ready: boolean): void => {
      this.permissionsReady = ready;
      if (!ready) {
        this.permissionService.load().subscribe({ error: (): void => undefined });
      }
    });

    // Subscribe to favorites changes (reactive, always listening)
    this.favoritesSubscription = this.favoritesService.favorites$.subscribe(favorites => {
      
      // Excluir master-dashboard aqu�
      this.favoriteMenuItems = favorites
        .sort((a, b) => a.order - b.order)
        .filter(fav => fav.route !== 'master-dashboard')
        .map(fav => ({
          displayName: fav.displayName,
          iconClass: fav.iconClass,
          route: fav.route
        }));
      this.updateFavoritesCategory();
    });

    // Get user profile ONCE and load favorites/menu ONCE
    this.auth.userProfile$.pipe(
      filter(res => res !== null && res !== undefined), // Only continue if there's data
      take(1), // ?? ONLY TAKE THE FIRST VALID EMISSION
      catchError((err) => {
        console.error('Error fetching user profile:', err);
        return of(null);
      })
    ).subscribe((res: any) => {
      
      
      if (res && res.currentTenant) {
        this.sideNaveUserId = res.currentTenant.tenant_id;
      }

      // Load favorites ONCE
      if (res && res.sub) {
        this.currentUserId = res.sub;
        
        this.favoritesService.loadFavorites(this.currentUserId).subscribe();
        // NUEVO: Cargar men� din�mico
        this.loadSubscriptionMenu();
      }
  
      // Load client settings
      if (this.sideNaveUserId) {
        this.api.clients.get(this.sideNaveUserId).pipe(
          catchError((err) => {
            console.error('Error fetching client data:', err);
            this.is4ComplyEnabled = true;
            this.is4SegmentsEnabled = true;
            this.is4PreferencesEnabled = true;
            return of(null);
          })
        ).subscribe((a) => {
          if (a) {
            this.is4ComplyEnabled = a.is4ComplyEnabled !== null && a.is4ComplyEnabled !== undefined ? a.is4ComplyEnabled : true;
            this.is4SegmentsEnabled = a.is4SegmentsEnabled !== null && a.is4SegmentsEnabled !== undefined ? a.is4SegmentsEnabled : true;
            this.is4PreferencesEnabled = a.is4PreferencesEnabled !== null && a.is4PreferencesEnabled !== undefined ? a.is4PreferencesEnabled : false;
            this.is4BridgeEnabled = a.is4BridgeEnabled !== null && a.is4BridgeEnabled !== undefined ? a.is4BridgeEnabled : false;
          }
        });
      } else {
        this.is4ComplyEnabled = true;
        this.is4SegmentsEnabled = true;
        this.is4PreferencesEnabled = true;
        this.is4BridgeEnabled = false;
      }
    });
  }

  private loadSubscriptionMenu() {
    if (!this.sideNaveUserId || !this.currentUserId) return;
    this.loadingMenu = true;
    this.menuError = null;
    this.menuSubscription = this.subscriptionMenuService.getMenu(
      String(this.sideNaveUserId),
      this.currentUserId
    ).subscribe({
      next: (menu) => {
        this.subscriptionMenu = menu;
        this.modules = (menu && menu.modules) ? menu.modules : [];
        this.overrideSegmentsUrl();
        this.loadingMenu = false;
        // Load saved state for dynamic modules
        this.loadMenuState();
      },
      error: (err) => {
        this.menuError = 'Error loading menu';
        this.loadingMenu = false;
      }
    });
  }

  ngOnDestroy() {
    // Cleanup subscriptions
    if (this.favoritesSubscription) {
      this.favoritesSubscription.unsubscribe();
    }
    if (this.menuSubscription) {
      this.menuSubscription.unsubscribe();
    }
    if (this.permissionsReadySubscription) {
      this.permissionsReadySubscription.unsubscribe();
    }
  }

  updateFavoritesCategory() {
    // Find and update Favorites category
    const favCategory = this.categories.find(c => c.name === 'Favorites');
    if (favCategory && favCategory.menu && favCategory.menu.length > 0) {
      // My Dashboard always first (no duplicados)
      const myDashboard: SideNavItem = {
        displayName: "SIDEMENU.MY_DASHBOARD",
        iconClass: "fas fa-chart-pie",
        route: "master-dashboard",
      };
      // Excluir master-dashboard de favoritos din�micos si llegara a estar
      const filteredFavorites = this.favoriteMenuItems.filter(fav => fav.route !== 'master-dashboard');
      favCategory.menu[0].menuOptions = [myDashboard, ...filteredFavorites];
    }
  }

  // Method to check if the category is enabled
  isCategoryEnabled(categoryName: string): boolean {
    switch (categoryName) {
      case 'Favorites':
        return true; // Always enabled
      case '4Comply':
        return this.is4ComplyEnabled;
      case '4Segments':
        return this.is4SegmentsEnabled;
      case '4Preferences':
        return this.is4PreferencesEnabled;
      case '4Bridge':
        return this.is4BridgeEnabled;
      default:
        return false;
    }
  }

  private overrideSegmentsUrl(): void {
    const segmentsUrl = environment.segments?.SEGMENTS_URL;
    if (!segmentsUrl) return;
    this.writeActiveTenantCookie();
    for (const mod of this.modules) {
      if (mod.code !== '4segments') continue;
      // A collapsed menu template carries the route on the module itself; templates that still have
      // the category and leaf carry it there. Both are overridden with the environment's URL.
      if (mod.isExternal) {
        mod.route = segmentsUrl;
      }
      for (const cat of mod.categories) {
        for (const item of cat.items) {
          if (item.isExternal) {
            item.route = segmentsUrl;
          }
        }
      }
    }
  }

  /**
   * Hands the active tenant to 4Segments. Written on the shared parent domain so it survives the
   * jump between `dashboard*.4comply.io` and `segments*.4comply.io`.
   */
  private writeActiveTenantCookie(): void {
    if (!this.sideNaveUserId) return;
    document.cookie = activeTenantCookie(window.location.hostname, String(this.sideNaveUserId), {
      secure: window.location.protocol === 'https:'
    });
  }

  /**
   * Second, independent gate alongside module.isEnabled (subscription): a module the tenant IS
   * subscribed to is still hidden here if the caller lacks any of its read permissions. See
   * isModuleVisibleByPermission (sidenav-module-navigation.ts) for the map/logic — the ONE place
   * it lives — and its readiness handling.
   */
  public isModulePermitted(module: ModuleMenuDto): boolean {
    return isModuleVisibleByPermission(
      module.code,
      this.permissionsReady,
      (permissionId: string): boolean => this.permissionService.has(permissionId)
    );
  }

  // Métodos para el template dinámico - muestran TODOS los items (accesibles y locked)
  getVisibleCategories(module: ModuleMenuDto): MenuCategoryDto[] {
    // Hide a category left with no permission-visible items (see getVisibleItems).
    return module.categories.filter((cat: MenuCategoryDto): boolean => this.getVisibleItems(cat).length > 0);
  }

  getVisibleItems(category: MenuCategoryDto): MenuItemDto[] {
    if (!category.items) return [];
    // Roles layer on top of subscription: hide an item whose screen the caller cannot read.
    // Fails open until the permission set loads (isItemVisibleByPermission) — the API still guards.
    return category.items.filter((item: MenuItemDto): boolean =>
      isItemVisibleByPermission(
        item.route,
        this.permissionsReady,
        (permissionId: string): boolean => this.permissionService.has(permissionId)
      )
    );
  }

  public readonly categories: MenuCategory[] = [
    {
      name: 'Favorites',
      iconClass: 'fas fa-star', // FontAwesome star icon
      isExpanded: true,
      isEnabled: true, // Always enabled
      menu: [
        {
          name: "", // Empty name to not show submenu
          expandedByDefault: true,
          menuOptions: [
            {
              displayName: "SIDEMENU.MY_DASHBOARD",
              iconClass: "fas fa-chart-pie",
              route: "master-dashboard",
            }
          ]
        }
      ]
    },
    {
      name: '4Comply',
      isExpanded: true,
      isEnabled: this.isCategoryEnabled("4Comply"),
      menu: [
        {
          name: "SIDEMENU.CIT_RIGHTS_FULFILLMENT",
          expandedByDefault: true,
          menuOptions: [
            {
              displayName: "SIDEMENU.RIGHTS_DASHBOARD",
              iconClass: "fas fa-tachometer-alt",
              route: "dashboard-rights",
            },
            {
              displayName: "SIDEMENU.RIGHTS_REQUESTS",
              iconClass: "fas fa-lightbulb",
              route: "right-requests",
            },
            {
              displayName: "SIDEMENU.RIGHTS_FLOWS",
                iconClass: "fa-solid fa-diagram-project",
              route: "rights-flows",
            }
          ]
        },
        {
          name: "SIDEMENU.COMPLIANCE_INPUTS",
          expandedByDefault: true,
          menuOptions: [
            {
              displayName: "SIDEMENU.COMPLIANCE_DASHBOARD",
              iconClass: "fas fa-tachometer-alt",
              route: "dashboard-consent",
            },
            {
              displayName: "SIDEMENU.COMPLIANCE_INPUTS",
              iconClass: "fas fa-door-open",
              route: "compliance-inputs",
            },
            {
              displayName: "SIDEMENU.PERMISSIONS",
                iconClass: "fa-light fa-shield-halved",
              route: "consent-permissions",
            },
            {
              displayName: "SIDEMENU.CITIZEN_HISTORY",
              iconClass: "fas fa-link",
              route: "citizen-history",
            }
          ]
        },
        {
          name: "SIDEMENU.LEGAL_ACTIVITIES_VAULT",
          expandedByDefault: false,
          menuOptions: [
            {
              displayName: "SIDEMENU.LEGAL_ACTIVITIES_VAULT",
              iconClass: "fas fa-balance-scale-right",
              route: "legal-activities",
            },
            {
              displayName: "SIDEMENU.LEGAL_ACTIVITY_ERRORS",
                iconClass: "fa-solid fa-circle-exclamation",
              route: "legal-activity-errors",
            },
            {
              displayName: "SIDEMENU.FORGOTTEN_VAULT",
              iconClass: "fas fa-chart-line",
              route: "forgotten-users",
            }
          ]
        },
        {
          name: "SIDEMENU.HITL",
          expandedByDefault: false,
          menuOptions: [
            {
              displayName: "SIDEMENU.HITL_TASKS",
              iconClass: "fas fa-link",
              route: "hitl-tasks",
            },
            {
              displayName: "SIDEMENU.HITL_CONFIGURATION",
              iconClass: "fas fa-link",
              route: "hitl-config",
            }
          ]
        },
        {
          name: "SIDEMENU.CONFIGURATION",
          expandedByDefault: false,
          menuOptions: [
            {
              displayName: "SIDEMENU.GEO",
              iconClass: "fas fa-globe",
              route: "geo",
            },
            {
              displayName: "SIDEMENU.REGULATIONS",
              iconClass: "fas fa-gavel",
              route: "client-regulations",
            },

            {
              displayName: "SIDEMENU.FORMS",
              iconClass: "far fa-file-alt",
              route: "forms",
            },
            {
              displayName: "SIDEMENU.EMAIL_TEMPLATES",
                iconClass: "fa-regular fa-envelope",
              route: "email-templates",
            },
            {
              displayName: "SIDEMENU.LANDING_PAGES",
                iconClass: "fa-regular fa-file-lines",
              route: "landing-pages",
            },
            {
              displayName: "SIDEMENU.WEBHOOKS",
                iconClass: "fa-solid fa-diagram-project",
              route: "webhooks",
            },
            {
              displayName: "SIDEMENU.SYSTEMS",
              iconClass: "fas fa-link",
              route: "systems",
            },
            {
              displayName: "SIDEMENU.BUSINESS_UNITS",
                iconClass: "fa-solid fa-briefcase",
              route: "business-units",
            }/*,
            {
              displayName: "SIDEMENU.BUSINESS",
              iconClass: "fas fa-link",
              route: "business-units",
            }*/
          ]
        }
      ]
    },
    {
      name: '4Preferences',
      isExpanded: true,
      isEnabled: this.isCategoryEnabled("4Preferences"),
      height:'22px',
      menu: [
        {
              name: "PREFERENCES.REPORT",
          expandedByDefault: true,
          menuOptions: [
            {
              displayName: "PREFERENCES.4PREFERENCES_DASHBOARD",
              iconClass: "fa-solid fa-chart-line",
              route: "preference-dashboard",
            },
            {
              displayName: "SIDEMENU.4PREFERENCES_MASTER_DASHBOARD",
              iconClass: "fa-solid fa-chart-pie",
              route: "master-dashboard",
            },
            {
              displayName: "SIDEMENU.4PREFERENCES_CONTACT_PREFERENCES",
                  iconClass: "fa-solid fa-chart-line",
              route: "contact-preference",
            },
            {
              displayName: "PREFERENCES.CONTACT_PREFERENCE_HISTORY",
                iconClass: "fa-solid fa-chart-line",
              route: "contact-preference-history",
            },
            {
              displayName: "PREFERENCES.CONTACT_DETAILS",
                iconClass: "fa-solid fa-chart-line",
              route: "contact-details",
            },
            {
              displayName: "PREFERENCES.CONTACT_DETAILS_HISTORY",
                iconClass: "fa-solid fa-chart-line",
              route: "contact-details-history",
            },
            {
              displayName: "PREFERENCES.FLOW_REPORT",
              iconClass: "fa-solid fa-diagram-project",
              route: "preference-flow-report",
            }
          ]
        },
        {
          name: "SIDEMENU.CONFIGURATION",
          expandedByDefault: false,
          menuOptions: [
            {
              displayName: "PREFERENCES.PREFERENCE_PAGES",
                  iconClass: "fa-regular fa-file-lines",
              route: "preference-page",
            },
            {
              displayName: "PREFERENCES.CORE_PREFERENCES",
                iconClass: "fa-solid fa-list-check",
              route: "core-preference",
            },
            {
              displayName: "PREFERENCES.CORE_CONTACTS",
                iconClass: "fa-solid fa-user-check",
              route: "core-contact",
            },
            {
              displayName: "PREFERENCES.TRANSLATIONS",
                iconClass: "fa-solid fa-language",
              route: "preference-translations",
            },
            {
              displayName: "PREFERENCES.PREFERENCE_CHANNELS",
                iconClass: "fa-regular fa-paper-plane",
              route: "preference-channel",
            },
            {
              displayName: "PREFERENCES.PREFERENCE_FREQUENCIES",
              iconClass: "fa-regular fa-calendar-check",
              route: "preference-frequency",
            },
            {
              displayName: "PREFERENCES.PREFERENCE_CATALOGS",
              iconClass: "fa fa-list",
              route: "preference-catalog",
            },
            {
                displayName: "PREFERENCES.PREFERENCE_WEBHOOKS",
                iconClass: "fa-solid fa-diagram-project",
                route: "preference-webhooks",
            },
            {
                displayName: "PREFERENCES.PREFERENCE_WHITELIST",
                iconClass: "fa-solid fa-unlock",
                route: "preference-whitelist",
            },
           /* {
              displayName: "*Core Preference Integration*",
                iconClass: "fa-solid fa-list-check",
              route: "core-preference-integration",
            },*/
            {
              displayName: "SIDEMENU.BUSINESS_UNITS",
                iconClass: "fa-solid fa-briefcase",
              route: "business-units",
            }
          ]
        }
      ]
    },
    {
      name: '4Segments',
      isExpanded: false,
      isEnabled: this.isCategoryEnabled("4Segments"),
      menu: [
        {
          name: "SIDEMENU.REPORTING_AND_SEGMENTATION",
          expandedByDefault: false,
          menuOptions: [
            {
              displayName: "4Segments",
              iconClass: "fas fa-chart-pie",
              route: environment.segments.SEGMENTS_URL,
            }
          ]
        }
      ]
    },
    {
      name: '4Bridge',
      iconClass: 'fas fa-bridge',
      isExpanded: true,
      isEnabled: this.isCategoryEnabled("4Bridge"),
      menu: [
        {
          name: "BRIDGE.DASHBOARD",
          expandedByDefault: true,
          menuOptions: [
            {
              displayName: "BRIDGE.BRIDGE_DASHBOARD",
              iconClass: "fas fa-tachometer-alt",
              route: "bridge-dashboard",
            }
          ]
        },
        {
          name: "BRIDGE.OPERATIONS",
          expandedByDefault: false,
          menuOptions: [
            {
              displayName: "BRIDGE.TASKS",
              iconClass: "fas fa-tasks",
              route: "bridge-tasks",
            },
            {
              displayName: "BRIDGE.WEBHOOKS",
              iconClass: "fas fa-satellite-dish",
              route: "bridge-webhooks",
            }
          ]
        },
        {
          name: "SIDEMENU.CONFIGURATION",
          expandedByDefault: true,
          menuOptions: [
            {
              displayName: "BRIDGE.CONNECTIONS",
              iconClass: "fas fa-plug",
              route: "bridge-connections",
            },
            {
              displayName: "BRIDGE.INTEGRATIONS",
              iconClass: "fas fa-exchange-alt",
              route: "bridge-integrations",
            },
            {
              displayName: "BRIDGE.NOTIFICATIONS",
              iconClass: "fas fa-bell",
              route: "bridge-notifications",
            },
            {
              displayName: "BRIDGE.CONNECTOR_METADATA",
              iconClass: "fas fa-database",
              route: "bridge-connector-metadata",
            }
          ]
        }
      ]
    }
  ];

  // Method to toggle the category visibility
  public toggleCategory(index: number): void {
    this.categories[index].isExpanded = !this.categories[index].isExpanded;
    this.saveMenuState();
  }

  // Method to toggle module visibility
  public toggleModule(module: ModuleMenuDto): void {
    module.isExpanded = !module.isExpanded;
    this.saveMenuState();
  }

  /**
   * A module that carries a route is a single destination: the header opens it. One that is gated
   * behind a feature the tenant does not have goes to the upsell, the same way a locked leaf item
   * does. Everything else keeps expanding.
   */
  public onModuleHeaderClick(module: ModuleMenuDto): void {
    const action = resolveModuleHeaderAction(module);

    if (action.kind === 'expand') {
      this.toggleModule(module);
      return;
    }

    if (action.kind === 'upgrade') {
      this.router.navigate(['settings'], { queryParams: { tab: 'subscription' } });
      return;
    }

    // Refreshed here rather than trusted from menu load, where it may already be an hour old.
    this.writeActiveTenantCookie();

    if (isAbsoluteUrl(action.route)) {
      window.open(action.route, '_blank');
    } else {
      this.router.navigate([action.route]);
    }
  }

  /** True when the header navigates, which means no caret and no collapsible block. */
  public moduleNavigates(module: ModuleMenuDto): boolean {
    return resolveModuleHeaderAction(module).kind !== 'expand';
  }

  /**
   * The module's wordmark, from our own origin, or nothing when the module has no logo — in which
   * case the template's *ngIf renders no image rather than a broken one. Deliberately ignores
   * `module.iconUrl`: see sidenav-template-origin.spec.ts.
   */
  public logoFor(module: ModuleMenuDto): string | undefined {
    return productLogoFor(module.code);
  }

  // Save menu state to localStorage
  private saveMenuState(): void {
    try {
      const state = {
        categories: this.categories.map(cat => ({
          name: cat.name,
          isExpanded: cat.isExpanded
        })),
        modules: this.modules.map(mod => ({
          name: mod.name,
          isExpanded: mod.isExpanded
        }))
      };
      localStorage.setItem(this.MENU_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving menu state to localStorage:', error);
    }
  }

  // Load menu state from localStorage
  private loadMenuState(): void {
    try {
      const savedState = localStorage.getItem(this.MENU_STATE_KEY);
      if (!savedState) return;

      const state = JSON.parse(savedState);

      // Restore categories state
      if (state.categories && Array.isArray(state.categories)) {
        state.categories.forEach((savedCat: any) => {
          const category = this.categories.find(c => c.name === savedCat.name);
          if (category) {
            category.isExpanded = savedCat.isExpanded;
          }
        });
      }

      // Restore modules state
      if (state.modules && Array.isArray(state.modules)) {
        state.modules.forEach((savedMod: any) => {
          const module = this.modules.find(m => m.name === savedMod.name);
          if (module) {
            module.isExpanded = savedMod.isExpanded;
          }
        });
      }
    } catch (error) {
      console.error('Error loading menu state from localStorage:', error);
    }
  }

  onLockedModuleClick(module: any): void {
    this.router.navigate(['settings'], { queryParams: { tab: 'subscription' } });
  }

  // Set para rutas en proceso de eliminaci�n
  removingFavorites = new Set<string>();

  // Elimina un favorito (no My Dashboard)
  removeFavorite(option: SideNavItem, event: Event) {
    event.stopPropagation();
    if (option.route === 'master-dashboard') return;
    if (!this.currentUserId) return;
    this.removingFavorites.add(option.route);
    this.favoritesService.removeFavorite(this.currentUserId, option.route).subscribe({
      next: () => this.removingFavorites.delete(option.route),
      error: () => this.removingFavorites.delete(option.route)
    });
  }
}
